---
id: terraform-module-review
title: Terraform Module Review
category: iac
difficulty: intermediate
tags:
  - terraform
  - review
  - best-practices
  - modules
  - hcl
description: >
  Reviews Terraform code for best practices, security issues, cost
  implications, and suggests improvements.
whenToUse: >
  Before merging Terraform changes — use as an automated code review
  step for infrastructure code.
variables:
  - name: terraform_code
    desc: The Terraform HCL code to review
  - name: provider
    desc: Cloud provider (AWS, Azure, GCP)
  - name: environment
    desc: Target environment (dev, staging, prod)
  - name: compliance
    desc: Compliance framework if any (SOC2, HIPAA, PCI-DSS, none)
tips:
  - Include the full module, not just the main.tf — variables.tf and outputs.tf matter for the review.
  - Specify the compliance framework to get relevant regulatory findings.
  - For large modules, review one resource group at a time.
---

You are a senior infrastructure engineer reviewing Terraform code.

## Module Code
```hcl
{{terraform_code}}
```

## Context
- Cloud provider: {{provider}}
- Environment: {{environment}}
- Compliance framework: {{compliance}}

## Review Checklist
Evaluate the code against these categories:

1. **Security** — Encryption, IAM least privilege, network exposure, secrets handling
2. **Reliability** — High availability, backup, disaster recovery, health checks
3. **Cost** — Right-sizing, reserved capacity opportunities, unused resources
4. **Operations** — Naming conventions, tagging, monitoring, logging
5. **Code Quality** — Module structure, variable validation, output usefulness, documentation

## Output Format (JSON)
{
  "overall_score": "A | B | C | D | F",
  "findings": [
    {
      "severity": "critical | high | medium | low | info",
      "category": "security | reliability | cost | operations | code_quality",
      "resource": "resource.name",
      "issue": "what's wrong",
      "recommendation": "how to fix it",
      "code_snippet": "suggested HCL"
    }
  ],
  "summary": "one-paragraph overall assessment"
}

<!-- EXAMPLE_OUTPUT -->

{
  "overall_score": "B",
  "findings": [
    {
      "severity": "high",
      "category": "security",
      "resource": "aws_s3_bucket.data",
      "issue": "S3 bucket has no server-side encryption configured",
      "recommendation": "Add aws_s3_bucket_server_side_encryption_configuration resource with AES256 or KMS",
      "code_snippet": "resource \"aws_s3_bucket_server_side_encryption_configuration\" \"data\" { ... }"
    }
  ],
  "summary": "Well-structured module with good naming conventions. Main concerns are around encryption at rest and overly permissive IAM policies."
}
